import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import './ReportSourcesView.css'; // Reuse existing CSS
import '@fortawesome/fontawesome-free/css/all.min.css';
import Loader from './components/Loader';
import PageInfo from './components/PageInfo';
import env from './utils/env';

// Define the interface for the SearchTarget data structure
interface SearchTarget {
    _id: string | null;
    issuer: string;
    category: string;
    value_in_million: string;
    currency: string;
    value_in_million_dollars: string;
    pricing_date: null;
    settlement_date: null;
    maturity_date: string | null;
    country: string;
    maturity_start_date: null;
    query: string;
    results: object | any[];
    urls: any[];
    search_timestamp: number | string | null;
    latestSearchTimestamp?: string | null; // New field from API
    latestSearchStatus?: string | null; // New field from API  
    type: string;
    source: string;
    processed: number | string | null;
    maturity_end_date: null;
    number_of_results: number;
    update_timestamp: null;
    market: string;
    max_number_results: number;
    identifier_type: string;
    identifier: string;
    name: string;
    jurisdiction_type: string;
    issuer_type: string;
    value: number;
    value_in_dollars: number;
    issuance_date: null;
    bond_url: string;
    program_url: string;
    insert_date: null;
    framework_url: string;
}

// Define the interface for a new search target with only allowed fields
interface NewSearchTarget {
    issuer: string;
    name: string;
    query: string;
    max_number_results: number;
    country: string;
    type?: string;
    source?: string;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ApiResponse {
    searchTargets: SearchTarget[];
    pagination?: PaginationMetadata;
}

// List of countries for dropdown
const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", 
    "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", 
    "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", 
    "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", 
    "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", 
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", 
    "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", 
    "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", 
    "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", 
    "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", 
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", 
    "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", 
    "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", 
    "Palau", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", 
    "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", 
    "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", 
    "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", 
    "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", 
    "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", 
    "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", 
    "Yemen", "Zambia", "Zimbabwe", "Supranational"
];

const SearchTargetsView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const [searchTargets, setSearchTargets] = useState<SearchTarget[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const limit = 100; // Number of items per page - increased from 10 to 100
    
    // State for popup and new target
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [newTarget, setNewTarget] = useState<NewSearchTarget>({
        issuer: '',
        name: '',
        query: '',
        max_number_results: 100, // Default value
        country: '', // Empty by default
        type: 'GENERIC_SEARCH',
        source: 'UI'
    });
    
    // State for details popup
    const [selectedTarget, setSelectedTarget] = useState<SearchTarget | null>(null);

    // Fetch search targets
    useEffect(() => {
        if (authLoaded) {
            fetchSearchTargets(currentPage);
        }
    }, [authLoaded, currentPage]);

    const fetchSearchTargets = (page: number) => {
        if (!authLoaded) return;
        
        setLoading(true);
        
        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        
        // Add authorization header if user is authenticated
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        axios.get<ApiResponse>(`${apiUrl}/search-targets?limit=${limit}&page=${page}`, {
            headers
        })
            .then(response => {
                if (Array.isArray(response.data.searchTargets)) {
                    const targets = response.data.searchTargets;
                    setSearchTargets(targets);
                } else {
                    console.error('Search targets is not an array:', response.data.searchTargets);
                    setSearchTargets([]);
                }
                
                // Set pagination metadata if available
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
                
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching search targets:', error);
                setLoading(false);
            });
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    // Format currency values
    const formatCurrency = (value: string | number) => {
        if (!value) return '-';
        return typeof value === 'number' 
            ? value.toLocaleString('en-US', { maximumFractionDigits: 2 })
            : value;
    };

    // Format date if it exists
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return new Intl.DateTimeFormat('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
            }).format(date);
        } catch (error) {
            console.warn(`Date parsing failed: ${dateString}`, error);
            return dateString;
        }
    };

    // Handler for input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewTarget(prev => ({
            ...prev,
            [name]: name === 'max_number_results' ? parseInt(value) || 100 : value
        }));
    };

    // Handler to add a new search target
    const handleAddTarget = () => {
        // Validate inputs - only query is required
        if (!newTarget.query.trim()) {
            alert('Query field is required');
            return;
        }
        
        if (newTarget.max_number_results < 1) {
            alert('Max number of results must be at least 1');
            return;
        }
        
        const headers: Record<string, string> = {
            'x-api-key': apiKey
        };
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        axios.post(`${apiUrl}/search-targets`, newTarget, {
            headers
        })
        .then(response => {
            // Assuming backend returns the created target
            setSearchTargets(prev => [...prev, response.data]);
            // Reset form and close popup
            setNewTarget({ 
                issuer: '', 
                name: '', 
                query: '',
                max_number_results: 100,
                country: '',
                type: 'GENERIC_SEARCH',
                source: 'UI'
            });
            setShowPopup(false);
            // Refresh the list to show the new target
            fetchSearchTargets(currentPage);
        })
        .catch(error => {
            console.error('Error adding new search target:', error);
            alert('Failed to add search target');
        });
    };
    
    // Handle row click to show details
    const handleRowClick = (target: SearchTarget) => {
        setSelectedTarget(target);
    };

    // CSS for truncated text cells
    const truncatedCellStyle = {
        maxWidth: '150px',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };
    
    // Styles for details popup
    const labelStyle = {
        textAlign: 'right' as const,
        paddingRight: '15px',
        fontWeight: 'bold'
    };
    
    const urlValueStyle = {
        textAlign: 'left' as const,
        wordBreak: 'break-word' as const,
        overflowWrap: 'break-word' as const
    };
    
    const detailsGridStyle = {
        display: 'grid',
        gridTemplateColumns: '180px auto',
        columnGap: '15px',
        rowGap: '10px',
        alignItems: 'baseline'
    };
    
    // Add a button to navigate to SearchResultsView prefiltered by target_id
    const handleNavigateToResults = (targetIds: string[]) => {
        const targetIdParam = targetIds.join(','); // Join array elements into a comma-separated string
        window.location.href = `/search-results?target_id=${targetIdParam}`;
    };

    // Show loading while auth is loading
    if (!authLoaded) {
        return <Loader text="Loading authentication..." />;
    }

    return (
        <div className="container">
            <h1>Search History</h1>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                width: '100%'
            }}>
                {/* Button to open popup - moved to top right */}
                <button 
                    onClick={() => setShowPopup(true)} 
                    style={{ 
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff', // Blue color
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        marginLeft: 'auto'
                    }}
                >
                    New
                </button>
            </div>
            
            {loading ? (
                <Loader text="Loading search history..." />
            ) : searchTargets.length > 0 ? (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: '540px' }}>Query</th>
                                <th>Category</th>
                                <th>Country</th>
                                <th style={{ width: '150px' }}>Issuer</th>
                                <th style={{ width: '150px' }}>Name</th>
                                <th>Last Search</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {searchTargets.map(target => (
                                <tr 
                                    key={target._id || target.identifier} 
                                    onClick={() => handleRowClick(target)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td style={truncatedCellStyle} title={target.query}>{target.query}</td>
                                    <td>{target.category}</td>
                                    <td>{target.country}</td>
                                    <td style={truncatedCellStyle} title={target.issuer}>{target.issuer}</td>
                                    <td style={truncatedCellStyle} title={target.name}>{target.name}</td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <div>
                                                {target.latestSearchTimestamp 
                                                    ? formatDate(target.latestSearchTimestamp)
                                                    : (typeof target.search_timestamp === 'string'
                                                        ? formatDate(target.search_timestamp)
                                                        : typeof target.search_timestamp === 'number'
                                                        ? formatDate(new Date(target.search_timestamp * 1000).toISOString())
                                                        : '-')
                                                }
                                            </div>
                                            {target.latestSearchStatus && (
                                                <div style={{ 
                                                    fontSize: '0.8em', 
                                                    color: target.latestSearchStatus === 'completed' ? '#28a745' : 
                                                          target.latestSearchStatus === 'failed' ? '#dc3545' : '#ffc107',
                                                    marginTop: '2px'
                                                }}>
                                                    {target.latestSearchStatus}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation(); // Stop the event from propagating to the parent row
                                                if (target._id) {
                                                    handleNavigateToResults([target._id]);
                                                } else {
                                                    console.error('Target ID is null');
                                                }
                                            }}
                                            style={{ 
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#007bff', // Blue color
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '3px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Show Results
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {/* Page Info Bar */}
                    {pagination && (
                        <PageInfo
                            count={searchTargets.length}
                            total={pagination.totalDocuments}
                            currentPage={pagination.currentPage}
                            totalPages={pagination.totalPages}
                            label={"search targets"}
                        />
                    )}
                    
                    {/* Pagination Controls */}
                    {pagination && (
                        <div className="pagination-controls">
                            <button 
                                onClick={() => handlePageChange(1)} 
                                disabled={!pagination.hasPreviousPage}
                                className="pagination-button"
                            >
                                <i className="fas fa-angle-double-left"></i>
                            </button>
                            
                            <button 
                                onClick={() => handlePageChange(pagination.currentPage - 1)} 
                                disabled={!pagination.hasPreviousPage}
                                className="pagination-button"
                            >
                                <i className="fas fa-angle-left"></i>
                            </button>
                            
                            <span className="pagination-page-info">Page {pagination.currentPage} of {pagination.totalPages}</span>
                            
                            <button 
                                onClick={() => handlePageChange(pagination.currentPage + 1)} 
                                disabled={!pagination.hasNextPage}
                                className="pagination-button"
                            >
                                <i className="fas fa-angle-right"></i>
                            </button>
                            
                            <button 
                                onClick={() => handlePageChange(pagination.totalPages)} 
                                disabled={!pagination.hasNextPage}
                                className="pagination-button"
                            >
                                <i className="fas fa-angle-double-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="no-results">No search history available.</div>
            )}
            
            {/* Add New Target Popup */}
            {showPopup && (
                <div className="popup-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div className="popup-content" style={{
                        backgroundColor: 'white',
                        padding: '2rem',
                        borderRadius: '5px',
                        width: '400px',
                        maxWidth: '90%'
                    }}>
                        <h2>Add New Search</h2>
                        <p>Add details for the allowed fields</p>
                        
                        {/* Name and issuer fields temporarily hidden
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                Issuer: <span style={{ color: '#888', fontSize: '0.9em' }}>(optional)</span>
                                <input 
                                    type="text"
                                    name="issuer"
                                    value={newTarget.issuer}
                                    onChange={handleInputChange}
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        marginTop: '0.25rem',
                                        border: '1px solid #ccc'
                                    }}
                                />
                            </label>
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                Name: <span style={{ color: '#888', fontSize: '0.9em' }}>(optional)</span>
                                <input 
                                    type="text"
                                    name="name"
                                    value={newTarget.name}
                                    onChange={handleInputChange}
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        marginTop: '0.25rem',
                                        border: '1px solid #ccc'
                                    }}
                                />
                            </label>
                        </div>
                        */}
                        
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                Query: <span style={{ color: '#666', fontSize: '0.9em' }}>(required)</span>
                                <input 
                                    type="text"
                                    name="query"
                                    value={newTarget.query}
                                    onChange={handleInputChange}
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        marginTop: '0.25rem',
                                        border: '1px solid #ccc'
                                    }}
                                    required
                                />
                            </label>
                        </div>
                        
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                Country: <span style={{ color: '#888', fontSize: '0.9em' }}>(optional)</span>
                                <select
                                    name="country"
                                    value={newTarget.country}
                                    onChange={handleInputChange}
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        marginTop: '0.25rem',
                                        border: '1px solid #ccc'
                                    }}
                                >
                                    <option value="">Select a country</option>
                                    {countries.map(country => (
                                        <option key={country} value={country}>
                                            {country}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                Max Results:
                                <input 
                                    type="number"
                                    name="max_number_results"
                                    value={newTarget.max_number_results}
                                    onChange={handleInputChange}
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        marginTop: '0.25rem',
                                        border: '1px solid #ccc'
                                    }}
                                    min="1"
                                    max="1000"
                                />
                            </label>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button 
                                onClick={() => setShowPopup(false)}
                                style={{ 
                                    padding: '0.5rem 1rem',
                                    backgroundColor: '#f0f0f0',
                                    border: '1px solid #ccc',
                                    borderRadius: '3px'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddTarget}
                                style={{ 
                                    padding: '0.5rem 1rem',
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px'
                                }}
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Details Popup */}
            {selectedTarget && (
                <div className="popup-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div className="popup-content" style={{
                        backgroundColor: 'white',
                        padding: '0',
                        borderRadius: '5px',
                        width: '600px',
                        maxWidth: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            backgroundColor: '#f0f0f0',
                            padding: '0.4rem 1rem',
                            borderTopLeftRadius: '5px',
                            borderTopRightRadius: '5px',
                            borderBottom: '1px solid #ddd'
                        }}>
                            <button
                                onClick={() => setSelectedTarget(null)}
                                style={{ 
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.3rem',
                                    cursor: 'pointer',
                                    lineHeight: '1'
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div style={{ padding: '1.5rem 2rem' }}>
                            <div style={{ ...detailsGridStyle, marginBottom: '20px' }}>
                                {selectedTarget._id && (
                                    <>
                                        <div style={labelStyle}>ID:</div>
                                        <div>{selectedTarget._id}</div>
                                    </>
                                )}
                                {selectedTarget.query && (
                                    <>
                                        <div style={labelStyle}>Query:</div>
                                        <div>{selectedTarget.query}</div>
                                    </>
                                )}
                                {selectedTarget.issuer && (
                                    <>
                                        <div style={labelStyle}>Issuer:</div>
                                        <div>{selectedTarget.issuer}</div>
                                    </>
                                )}
                                {selectedTarget.name && (
                                    <>
                                        <div style={labelStyle}>Name:</div>
                                        <div>{selectedTarget.name}</div>
                                    </>
                                )}
                                {selectedTarget.category && (
                                    <>
                                        <div style={labelStyle}>Category:</div>
                                        <div>{selectedTarget.category}</div>
                                    </>
                                )}
                                {selectedTarget.country && (
                                    <>
                                        <div style={labelStyle}>Country:</div>
                                        <div>{selectedTarget.country}</div>
                                    </>
                                )}
                                {selectedTarget.market && (
                                    <>
                                        <div style={labelStyle}>Market:</div>
                                        <div>{selectedTarget.market}</div>
                                    </>
                                )}
                                {selectedTarget.type && (
                                    <>
                                        <div style={labelStyle}>Type:</div>
                                        <div>{selectedTarget.type}</div>
                                    </>
                                )}
                            
                                {selectedTarget.value && (
                                    <>
                                        <div style={labelStyle}>Value:</div>
                                        <div>{formatCurrency(selectedTarget.value)}</div>
                                    </>
                                )}
                                {selectedTarget.value_in_dollars && (
                                    <>
                                        <div style={labelStyle}>Value in Dollars:</div>
                                        <div>{formatCurrency(selectedTarget.value_in_dollars)}</div>
                                    </>
                                )}
                                {selectedTarget.value_in_million && (
                                    <>
                                        <div style={labelStyle}>Value in Million:</div>
                                        <div>{selectedTarget.value_in_million} {selectedTarget.currency || ''}</div>
                                    </>
                                )}
                                {selectedTarget.value_in_million_dollars && (
                                    <>
                                        <div style={labelStyle}>Value in Million Dollars:</div>
                                        <div>{selectedTarget.value_in_million_dollars}</div>
                                    </>
                                )}
                                {selectedTarget.currency && (
                                    <>
                                        <div style={labelStyle}>Currency:</div>
                                        <div>{selectedTarget.currency}</div>
                                    </>
                                )}
                            
                                {(selectedTarget.latestSearchTimestamp || selectedTarget.search_timestamp) && (
                                    <>
                                        <div style={labelStyle}>Last Search:</div>
                                        <div>
                                            <div>
                                                {selectedTarget.latestSearchTimestamp 
                                                    ? formatDate(selectedTarget.latestSearchTimestamp)
                                                    : (typeof selectedTarget.search_timestamp === 'string'
                                                        ? formatDate(selectedTarget.search_timestamp)
                                                        : typeof selectedTarget.search_timestamp === 'number'
                                                        ? formatDate(new Date(selectedTarget.search_timestamp * 1000).toISOString())
                                                        : '-')
                                                }
                                            </div>
                                            {selectedTarget.latestSearchStatus && (
                                                <div style={{ 
                                                    fontSize: '0.8em', 
                                                    color: selectedTarget.latestSearchStatus === 'completed' ? '#28a745' : 
                                                          selectedTarget.latestSearchStatus === 'failed' ? '#dc3545' : '#ffc107',
                                                    marginTop: '2px'
                                                }}>
                                                    Status: {selectedTarget.latestSearchStatus}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                                {selectedTarget.maturity_date && (
                                    <>
                                        <div style={labelStyle}>Maturity Date:</div>
                                        <div>{formatDate(selectedTarget.maturity_date)}</div>
                                    </>
                                )}
                                
                                {selectedTarget.bond_url && (
                                    <>
                                        <div style={labelStyle}>Bond URL:</div>
                                        <div style={urlValueStyle}>
                                            <a href={selectedTarget.bond_url} target="_blank" rel="noopener noreferrer">{selectedTarget.bond_url}</a>
                                        </div>
                                    </>
                                )}
                                {selectedTarget.program_url && (
                                    <>
                                        <div style={labelStyle}>Program URL:</div>
                                        <div style={urlValueStyle}>
                                            <a href={selectedTarget.program_url} target="_blank" rel="noopener noreferrer">{selectedTarget.program_url}</a>
                                        </div>
                                    </>
                                )}
                                {selectedTarget.framework_url && (
                                    <>
                                        <div style={labelStyle}>Framework URL:</div>
                                        <div style={urlValueStyle}>
                                            <a href={selectedTarget.framework_url} target="_blank" rel="noopener noreferrer">{selectedTarget.framework_url}</a>
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            {(selectedTarget.urls && selectedTarget.urls.length > 0) && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ marginBottom: '0.5rem', textAlign: 'left' }}>URLs</h3>
                                    <ul style={{ paddingLeft: '1.5rem' }}>
                                        {selectedTarget.urls.map((url: string, index: number) => (
                                            <li key={index} style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                                <button 
                                    onClick={() => setSelectedTarget(null)}
                                    style={{ 
                                        padding: '0.5rem 1.5rem',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchTargetsView;
